import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Trophy,
  Target,
  Zap,
  Users,
  Calendar,
  TrendingUp,
  Star,
  Crown,
  Flame,
  Medal,
  Gift,
  PartyPopper,
  Sparkles,
  ChevronRight,
  X,
  Bell,
  CheckCircle2,
  Clock,
  MessageSquare,
} from 'lucide-react';

// ============================================
// MILESTONE DEFINITIONS
// ============================================

const MILESTONE_TIERS = {
  bronze: { color: '#cd7f32', glow: 'rgba(205,127,50,0.4)', icon: Medal, label: 'Bronze' },
  silver: { color: '#c0c0c0', glow: 'rgba(192,192,192,0.4)', icon: Medal, label: 'Silver' },
  gold: { color: '#ffd700', glow: 'rgba(255,215,0,0.5)', icon: Trophy, label: 'Gold' },
  platinum: { color: '#e5e4e2', glow: 'rgba(229,228,226,0.5)', icon: Crown, label: 'Platinum' },
  diamond: { color: '#b9f2ff', glow: 'rgba(185,242,255,0.6)', icon: Star, label: 'Diamond' },
};

const MILESTONES = {
  daily: [
    { id: 'daily-10', target: 10, tier: 'bronze', title: 'Morning Warm-up', description: '10 customers served today' },
    { id: 'daily-25', target: 25, tier: 'silver', title: 'Gaining Momentum', description: '25 customers served today' },
    { id: 'daily-50', target: 50, tier: 'gold', title: 'Halfway Hero', description: '50 customers served today' },
    { id: 'daily-100', target: 100, tier: 'platinum', title: 'Century Club', description: '100 customers served today' },
    { id: 'daily-200', target: 200, tier: 'diamond', title: 'Unstoppable Force', description: '200 customers served today' },
  ],
  weekly: [
    { id: 'weekly-100', target: 100, tier: 'bronze', title: 'Week Starter', description: '100 customers this week' },
    { id: 'weekly-250', target: 250, tier: 'silver', title: 'Building Momentum', description: '250 customers this week' },
    { id: 'weekly-500', target: 500, tier: 'gold', title: 'Midweek Champion', description: '500 customers this week' },
    { id: 'weekly-1000', target: 1000, tier: 'platinum', title: 'Week Warrior', description: '1,000 customers this week' },
    { id: 'weekly-2000', target: 2000, tier: 'diamond', title: 'Legendary Week', description: '2,000 customers this week' },
  ],
  monthly: [
    { id: 'monthly-500', target: 500, tier: 'bronze', title: 'Month Kickoff', description: '500 customers this month' },
    { id: 'monthly-1000', target: 1000, tier: 'silver', title: 'Rising Star', description: '1,000 customers this month' },
    { id: 'monthly-2500', target: 2500, tier: 'gold', title: 'Monthly MVP', description: '2,500 customers this month' },
    { id: 'monthly-5000', target: 5000, tier: 'platinum', title: 'Elite Performer', description: '5,000 customers this month' },
    { id: 'monthly-10000', target: 10000, tier: 'diamond', title: 'Hall of Fame', description: '10,000 customers this month' },
  ],
  allTime: [
    { id: 'total-1000', target: 1000, tier: 'bronze', title: 'First Thousand', description: '1,000 total customers' },
    { id: 'total-5000', target: 5000, tier: 'silver', title: 'Growing Strong', description: '5,000 total customers' },
    { id: 'total-10000', target: 10000, tier: 'gold', title: 'Ten K Club', description: '10,000 total customers' },
    { id: 'total-50000', target: 50000, tier: 'platinum', title: 'Fifty Thousand', description: '50,000 total customers' },
    { id: 'total-100000', target: 100000, tier: 'diamond', title: 'Legend Status', description: '100,000 total customers' },
  ],
  messages: [
    { id: 'msg-100', target: 100, tier: 'bronze', title: 'First Hundred', description: '100 messages sent today' },
    { id: 'msg-500', target: 500, tier: 'silver', title: 'Message Machine', description: '500 messages sent today' },
    { id: 'msg-1000', target: 1000, tier: 'gold', title: 'Thousand Replies', description: '1,000 messages sent today' },
    { id: 'msg-2500', target: 2500, tier: 'platinum', title: 'Communication Pro', description: '2,500 messages sent today' },
    { id: 'msg-5000', target: 5000, tier: 'diamond', title: 'Messaging Legend', description: '5,000 messages sent today' },
  ],
  streaks: [
    { id: 'streak-3', target: 3, tier: 'bronze', title: 'Hot Streak', description: '3-day winning streak' },
    { id: 'streak-7', target: 7, tier: 'silver', title: 'Week Warrior', description: '7-day winning streak' },
    { id: 'streak-14', target: 14, tier: 'gold', title: 'Fortnight Fire', description: '14-day winning streak' },
    { id: 'streak-30', target: 30, tier: 'platinum', title: 'Monthly Master', description: '30-day winning streak' },
    { id: 'streak-100', target: 100, tier: 'diamond', title: 'Centurion', description: '100-day winning streak' },
  ],
};

const CELEBRATION_MESSAGES = [
  "Amazing work, team! 🎉",
  "You're on fire! 🔥",
  "Incredible achievement! ⭐",
  "The team is unstoppable! 💪",
  "Another milestone crushed! 🚀",
  "Champions in action! 🏆",
  "Excellence delivered! ✨",
  "Making history! 📈",
];

// ============================================
// CONFETTI EFFECT
// ============================================

const Confetti = ({ active }) => {
  if (!active) return null;

  const confettiPieces = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 2,
    duration: 2 + Math.random() * 2,
    color: ['#ff4d4d', '#fbbf24', '#22c55e', '#3b82f6', '#a855f7', '#ec4899'][Math.floor(Math.random() * 6)],
    size: 8 + Math.random() * 8,
    rotation: Math.random() * 360,
  }));

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {confettiPieces.map((piece) => (
        <div
          key={piece.id}
          className="absolute animate-confetti-fall"
          style={{
            left: `${piece.left}%`,
            top: '-20px',
            width: piece.size,
            height: piece.size,
            backgroundColor: piece.color,
            borderRadius: Math.random() > 0.5 ? '50%' : '2px',
            transform: `rotate(${piece.rotation}deg)`,
            animationDelay: `${piece.delay}s`,
            animationDuration: `${piece.duration}s`,
          }}
        />
      ))}
    </div>
  );
};

// ============================================
// CELEBRATION MODAL
// ============================================

const CelebrationModal = ({ milestone, onClose, stats }) => {
  const tier = MILESTONE_TIERS[milestone.tier];
  const TierIcon = tier.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />

      <div
        className="relative w-full max-w-md rounded-3xl border-2 p-8 text-center animate-celebration-pop"
        style={{
          borderColor: tier.color,
          background: `linear-gradient(135deg, #0d1424 0%, #1a1a2e 100%)`,
          boxShadow: `0 0 60px ${tier.glow}, 0 0 120px ${tier.glow}`,
        }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-white/40 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Celebration Icon */}
        <div
          className="mx-auto mb-6 w-24 h-24 rounded-full flex items-center justify-center animate-bounce-slow"
          style={{
            background: `linear-gradient(135deg, ${tier.color}40 0%, ${tier.color}20 100%)`,
            boxShadow: `0 0 40px ${tier.glow}`,
          }}
        >
          <TierIcon className="w-12 h-12" style={{ color: tier.color }} />
        </div>

        {/* Title */}
        <div className="mb-2 flex items-center justify-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-400" />
          <span
            className="text-sm font-bold uppercase tracking-wider"
            style={{ color: tier.color }}
          >
            {tier.label} Milestone
          </span>
          <Sparkles className="w-5 h-5 text-amber-400" />
        </div>

        <h2 className="text-3xl font-bold text-white mb-2">{milestone.title}</h2>
        <p className="text-white/60 mb-6">{milestone.description}</p>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="rounded-xl bg-white/5 p-3">
            <p className="text-2xl font-bold text-white">{stats.today}</p>
            <p className="text-[10px] uppercase tracking-wider text-white/40">Today</p>
          </div>
          <div className="rounded-xl bg-white/5 p-3">
            <p className="text-2xl font-bold text-white">{stats.week}</p>
            <p className="text-[10px] uppercase tracking-wider text-white/40">This Week</p>
          </div>
          <div className="rounded-xl bg-white/5 p-3">
            <p className="text-2xl font-bold text-white">{stats.month}</p>
            <p className="text-[10px] uppercase tracking-wider text-white/40">This Month</p>
          </div>
        </div>

        {/* Message */}
        <p className="text-lg text-amber-300 font-medium mb-6">
          {CELEBRATION_MESSAGES[Math.floor(Math.random() * CELEBRATION_MESSAGES.length)]}
        </p>

        <button
          onClick={onClose}
          className="w-full py-3 rounded-xl font-semibold text-white transition-all hover:scale-105"
          style={{
            background: `linear-gradient(135deg, ${tier.color} 0%, ${tier.color}cc 100%)`,
          }}
        >
          Keep Going! 🚀
        </button>
      </div>
    </div>
  );
};

// ============================================
// MILESTONE PROGRESS CARD
// ============================================

const MilestoneProgressCard = ({ milestone, current, onCelebrate }) => {
  const tier = MILESTONE_TIERS[milestone.tier];
  const TierIcon = tier.icon;
  const progress = Math.min(100, (current / milestone.target) * 100);
  const isCompleted = current >= milestone.target;

  return (
    <div
      className={`rounded-2xl border p-4 transition-all ${
        isCompleted
          ? 'border-emerald-500/50 bg-emerald-500/10'
          : 'border-white/10 bg-white/5 hover:bg-white/10'
      }`}
      style={isCompleted ? { boxShadow: `0 0 20px ${tier.glow}` } : {}}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${tier.color}20` }}
          >
            <TierIcon className="w-5 h-5" style={{ color: tier.color }} />
          </div>
          <div>
            <p className="text-sm font-semibold text-white">{milestone.title}</p>
            <p className="text-xs text-white/50">{milestone.description}</p>
          </div>
        </div>
        {isCompleted && (
          <button
            onClick={() => onCelebrate(milestone)}
            className="p-2 rounded-lg bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 transition-colors"
          >
            <PartyPopper className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="flex items-center gap-3">
        <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${progress}%`,
              background: isCompleted
                ? `linear-gradient(90deg, #22c55e, #10b981)`
                : `linear-gradient(90deg, ${tier.color}, ${tier.color}aa)`,
            }}
          />
        </div>
        <span className={`text-sm font-bold ${isCompleted ? 'text-emerald-400' : 'text-white/70'}`}>
          {current.toLocaleString()}/{milestone.target.toLocaleString()}
        </span>
      </div>
    </div>
  );
};

// ============================================
// STATS OVERVIEW
// ============================================

const StatsOverview = ({ stats, streak }) => (
  <div className="mb-6">
    {/* Customers Stats */}
    <div className="mb-3">
      <h4 className="text-[10px] uppercase tracking-wider text-white/40 mb-2 flex items-center gap-2">
        <Users className="w-3 h-3" />
        Customers Served
      </h4>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-blue-500/10 to-blue-600/5 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-blue-400" />
            <span className="text-[10px] uppercase tracking-wider text-white/40">Today</span>
          </div>
          <p className="text-2xl font-bold text-white">{(stats.today || 0).toLocaleString()}</p>
          <p className="text-xs text-emerald-400 flex items-center gap-1 mt-1">
            <TrendingUp className="w-3 h-3" />
            +{stats.todayGrowth || 0}%
          </p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-purple-500/10 to-purple-600/5 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-purple-400" />
            <span className="text-[10px] uppercase tracking-wider text-white/40">This Week</span>
          </div>
          <p className="text-2xl font-bold text-white">{(stats.week || 0).toLocaleString()}</p>
          <p className="text-xs text-emerald-400 flex items-center gap-1 mt-1">
            <TrendingUp className="w-3 h-3" />
            +{stats.weekGrowth || 0}%
          </p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-amber-500/10 to-amber-600/5 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-amber-400" />
            <span className="text-[10px] uppercase tracking-wider text-white/40">This Month</span>
          </div>
          <p className="text-2xl font-bold text-white">{(stats.month || 0).toLocaleString()}</p>
          <p className="text-xs text-emerald-400 flex items-center gap-1 mt-1">
            <TrendingUp className="w-3 h-3" />
            +{stats.monthGrowth || 0}%
          </p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-rose-500/10 to-rose-600/5 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Flame className="w-4 h-4 text-rose-400" />
            <span className="text-[10px] uppercase tracking-wider text-white/40">Streak</span>
          </div>
          <p className="text-2xl font-bold text-white">{streak} days</p>
          <p className="text-xs text-amber-400 flex items-center gap-1 mt-1">
            <Star className="w-3 h-3" />
            Keep it up!
          </p>
        </div>
      </div>
    </div>

    {/* Messages Stats */}
    <div>
      <h4 className="text-[10px] uppercase tracking-wider text-white/40 mb-2 flex items-center gap-2">
        <MessageSquare className="w-3 h-3" />
        Messages Sent
      </h4>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-cyan-400" />
            <span className="text-[10px] uppercase tracking-wider text-white/40">Today</span>
          </div>
          <p className="text-2xl font-bold text-white">{(stats.todayMessages || 0).toLocaleString()}</p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-indigo-500/10 to-indigo-600/5 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-indigo-400" />
            <span className="text-[10px] uppercase tracking-wider text-white/40">This Week</span>
          </div>
          <p className="text-2xl font-bold text-white">{(stats.weekMessages || 0).toLocaleString()}</p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-pink-500/10 to-pink-600/5 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-pink-400" />
            <span className="text-[10px] uppercase tracking-wider text-white/40">This Month</span>
          </div>
          <p className="text-2xl font-bold text-white">{(stats.monthMessages || 0).toLocaleString()}</p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="w-4 h-4 text-emerald-400" />
            <span className="text-[10px] uppercase tracking-wider text-white/40">All Time</span>
          </div>
          <p className="text-2xl font-bold text-white">{(stats.allTimeMessages || 0).toLocaleString()}</p>
        </div>
      </div>
    </div>
  </div>
);

// ============================================
// RECENT ACHIEVEMENTS
// ============================================

const RecentAchievements = ({ achievements }) => (
  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <Trophy className="w-4 h-4 text-amber-400" />
        <h3 className="text-sm font-semibold text-white">Recent Achievements</h3>
      </div>
      <span className="text-xs text-white/40">{achievements.length} unlocked</span>
    </div>

    <div className="space-y-2">
      {achievements.slice(0, 5).map((achievement) => {
        const tier = MILESTONE_TIERS[achievement.tier];
        return (
          <div
            key={achievement.id}
            className="flex items-center gap-3 p-2 rounded-xl bg-white/5"
          >
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${tier.color}20` }}
            >
              <CheckCircle2 className="w-4 h-4" style={{ color: tier.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{achievement.title}</p>
              <p className="text-[10px] text-white/40">{achievement.unlockedAt}</p>
            </div>
            <span
              className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full"
              style={{ backgroundColor: `${tier.color}20`, color: tier.color }}
            >
              {tier.label}
            </span>
          </div>
        );
      })}
    </div>
  </div>
);

// ============================================
// MAIN MILESTONE TRACKER
// ============================================

const MilestoneTracker = ({
  stats: propStats,
  onStatsUpdate,
  isOpen,
  onClose,
}) => {
  // Demo stats (replace with real data)
  const [stats, setStats] = useState(propStats || {
    today: 47,
    week: 312,
    month: 1247,
    allTime: 8934,
    todayGrowth: 12,
    weekGrowth: 8,
    monthGrowth: 15,
  });
  const [streak, setStreak] = useState(7);
  const [celebratingMilestone, setCelebratingMilestone] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [achievements, setAchievements] = useState([
    { ...MILESTONES.daily[0], unlockedAt: 'Today, 9:30 AM' },
    { ...MILESTONES.daily[1], unlockedAt: 'Today, 11:45 AM' },
    { ...MILESTONES.weekly[0], unlockedAt: 'Yesterday' },
    { ...MILESTONES.monthly[0], unlockedAt: '3 days ago' },
  ]);
  const [activeTab, setActiveTab] = useState('daily');

  const handleCelebrate = useCallback((milestone) => {
    setCelebratingMilestone(milestone);
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 4000);
  }, []);

  // Check for new milestones
  useEffect(() => {
    const checkMilestones = (category, current) => {
      const categoryMilestones = MILESTONES[category];
      if (!categoryMilestones) return;
      categoryMilestones.forEach((milestone) => {
        if (current >= milestone.target && !achievements.find(a => a.id === milestone.id)) {
          // New milestone reached!
          handleCelebrate(milestone);
          setAchievements(prev => [
            { ...milestone, unlockedAt: 'Just now!' },
            ...prev,
          ]);
        }
      });
    };

    checkMilestones('daily', stats.today || 0);
    checkMilestones('weekly', stats.week || 0);
    checkMilestones('monthly', stats.month || 0);
    checkMilestones('allTime', stats.allTime || 0);
    checkMilestones('messages', stats.todayMessages || 0);
  }, [stats, achievements, handleCelebrate]);

  if (!isOpen) return null;

  const currentMilestones = MILESTONES[activeTab];
  const currentValue = {
    daily: stats.today,
    weekly: stats.week,
    monthly: stats.month,
    allTime: stats.allTime,
    messages: stats.todayMessages || 0,
    streaks: streak,
  }[activeTab];

  return (
    <>
      <Confetti active={showConfetti} />

      {celebratingMilestone && (
        <CelebrationModal
          milestone={celebratingMilestone}
          stats={stats}
          onClose={() => setCelebratingMilestone(null)}
        />
      )}

      <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

        <div className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-3xl border border-white/10 bg-[#0d1424] shadow-2xl">
          {/* Header */}
          <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Team Milestones</h2>
                <p className="text-xs text-white/50">Track achievements and celebrate wins</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/5 rounded-lg text-white/40 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
            {/* Stats Overview */}
            <StatsOverview stats={stats} streak={streak} />

            {/* Tab Navigation */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              {[
                { id: 'daily', label: 'Daily', icon: Calendar },
                { id: 'weekly', label: 'Weekly', icon: Zap },
                { id: 'monthly', label: 'Monthly', icon: Target },
                { id: 'allTime', label: 'All Time', icon: Trophy },
                { id: 'messages', label: 'Messages', icon: MessageSquare },
                { id: 'streaks', label: 'Streaks', icon: Flame },
              ].map((tab) => {
                const TabIcon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                      activeTab === tab.id
                        ? 'bg-[#ff4d4d] text-white'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <TabIcon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
              {/* Milestones */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-white/70 uppercase tracking-wider mb-4">
                  {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Milestones
                </h3>
                {currentMilestones.map((milestone) => (
                  <MilestoneProgressCard
                    key={milestone.id}
                    milestone={milestone}
                    current={currentValue}
                    onCelebrate={handleCelebrate}
                  />
                ))}
              </div>

              {/* Recent Achievements */}
              <div>
                <RecentAchievements achievements={achievements} />

                {/* Quick Actions */}
                <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4">
                  <h3 className="text-sm font-semibold text-white mb-3">Quick Actions</h3>
                  <div className="space-y-2">
                    <button className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors text-left">
                      <Bell className="w-4 h-4 text-blue-400" />
                      <span className="text-sm text-white">Set milestone alerts</span>
                      <ChevronRight className="w-4 h-4 text-white/30 ml-auto" />
                    </button>
                    <button className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors text-left">
                      <Gift className="w-4 h-4 text-purple-400" />
                      <span className="text-sm text-white">Create custom milestone</span>
                      <ChevronRight className="w-4 h-4 text-white/30 ml-auto" />
                    </button>
                    <button className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors text-left">
                      <Users className="w-4 h-4 text-emerald-400" />
                      <span className="text-sm text-white">Share with team</span>
                      <ChevronRight className="w-4 h-4 text-white/30 ml-auto" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

MilestoneTracker.propTypes = {
  stats: PropTypes.shape({
    today: PropTypes.number,
    week: PropTypes.number,
    month: PropTypes.number,
    allTime: PropTypes.number,
    todayMessages: PropTypes.number,
    weekMessages: PropTypes.number,
    monthMessages: PropTypes.number,
    allTimeMessages: PropTypes.number,
    todayGrowth: PropTypes.number,
    weekGrowth: PropTypes.number,
    monthGrowth: PropTypes.number,
  }),
  onStatsUpdate: PropTypes.func,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MilestoneTracker;
